import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { listFiltersService, ListTableSort, SortOrder } from "sprint-common-web";

export interface LanguagesListingFilters {
    page: number;
    pageSize: number;
    sortBy: string;
    sortOrder: SortOrder;
    name: string;
    code: string;
    subtitlesOnly: boolean;
    dubbingsOnly: boolean;
}

const defaultFilters: LanguagesListingFilters = {
    page: 1,
    pageSize: 50,
    sortBy: "name",
    sortOrder: "asc",
    name: "",
    code: "",
    subtitlesOnly: false,
    dubbingsOnly: false
};

export const useLanguagesListingFilters = () => {
    const filtersService = useMemo(() => listFiltersService<LanguagesListingFilters>(defaultFilters), []);
    const location = useLocation();
    const history = useHistory();

    const [filters, setFilters] = useState<LanguagesListingFilters>(() =>
        filtersService.getSavedFilters(location.search)
    );

    useEffect(() => {
        filtersService.saveFilters(location.pathname, history, filters);
    }, [location.pathname, history, filters]);

    const setSort = (sort: ListTableSort) => {
        setFilters(
            (prevFilters: LanguagesListingFilters): LanguagesListingFilters => ({
                ...prevFilters,
                sortBy: sort.by,
                sortOrder: sort.order,
                page: 1
            })
        );
    };

    const setPage = (page: number) => {
        setFilters((prevFilters: LanguagesListingFilters): LanguagesListingFilters => ({ ...prevFilters, page }));
    };

    const setPageSize = (pageSize: number): void => {
        setFilters(
            (prevFilters: LanguagesListingFilters): LanguagesListingFilters => ({ ...prevFilters, pageSize, page: 1 })
        );
    };

    const setName = (name: string): void => {
        setFilters(
            (prevFilters: LanguagesListingFilters): LanguagesListingFilters => ({ ...prevFilters, name, page: 1 })
        );
    };

    const setCode = (code: string): void => {
        setFilters(
            (prevFilters: LanguagesListingFilters): LanguagesListingFilters => ({
                ...prevFilters,
                code,
                page: 1
            })
        );
    };

    const toggleSubtitlesOnly = (): void => {
        setFilters(
            (prevFilters: LanguagesListingFilters): LanguagesListingFilters => ({
                ...prevFilters,
                page: 1,
                subtitlesOnly: !prevFilters.subtitlesOnly
            })
        );
    };

    const toggleDubbingsOnly = (): void => {
        setFilters(
            (prevFilters: LanguagesListingFilters): LanguagesListingFilters => ({
                ...prevFilters,
                page: 1,
                dubbingsOnly: !prevFilters.dubbingsOnly
            })
        );
    };

    const clearFilters = (): void => {
        setFilters(defaultFilters);
    };

    return {
        filters,
        setSort,
        setPage,
        setPageSize,
        setName,
        setCode,
        toggleSubtitlesOnly,
        toggleDubbingsOnly,
        clearFilters
    };
};
